import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Can } from "../../../components/Can";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmpresa } from "../../../contexts/EmpresaContext";
import { useIsMobile } from "../../../hooks/useIsMobile";

import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";
import FiltroBar from "../../../components/FiltroBar/FiltroBar";
import CardListMobile from "../../../templates/mobile/CardListMobile";

import { db } from "../../../config/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
} from "firebase/firestore";

function renderCodigo(f) {
  if (f.codigo) return f.codigo;
  if (f.codigoSequencial != null) {
    return `FN${String(f.codigoSequencial).padStart(4, "0")}`;
  }
  if (f.id) return f.id.slice(0, 8);
  return "-";
}

function FornecedoresPage() {
  const { modoGlobal, empresaAtual } = useEmpresa();
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  const [fornecedores, setFornecedores] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [sidebarAberta, setSidebarAberta] = useState(false);

  const [campoFiltro, setCampoFiltro] = useState("nome");
  const [textoFiltro, setTextoFiltro] = useState("");

  const camposFiltro = [
    { value: "codigo", label: "Código" },
    { value: "nome", label: "Nome" },
    { value: "contato", label: "Contato" },
    { value: "telefone", label: "Telefone" },
    { value: "email", label: "Email" },
    { value: "endereco", label: "Endereço" },
  ];

  useEffect(() => {
    async function carregarFornecedores() {
      if (!usuarioLogado) return;

      setCarregando(true);
      setErro("");

      try {
        const fornecedoresRef = collection(db, "fornecedores");
        let q;

        if (usuarioLogado.grupoId) {
          if (!modoGlobal && empresaAtual?.id) {
            // filtra por grupo + empresa quando disponível
            q = query(
              fornecedoresRef,
              where("grupoId", "==", usuarioLogado.grupoId),
              where("empresaId", "==", empresaAtual.id)
            );
          } else {
            // grupo sem empresa atual
            q = query(
              fornecedoresRef,
              where("grupoId", "==", usuarioLogado.grupoId)
            );
          }
        } else {
          // fallback: não mostra nada se não tiver grupo
          q = query(fornecedoresRef, where("codigo", "==", "__nulo__"));
        }

        const snap = await getDocs(q);
        const lista = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        setFornecedores(lista);
      } catch (e) {
        console.error("Erro ao carregar fornecedores:", e);
        setErro("Erro ao carregar fornecedores. Tente novamente.");
      } finally {
        setCarregando(false);
      }
    }

    carregarFornecedores();
  }, [usuarioLogado, modoGlobal, empresaAtual?.id]);

  const fornecedoresFiltrados = fornecedores.filter((f) => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return true;

    let valorBase = "";

    if (campoFiltro === "codigo") {
      valorBase = renderCodigo(f);
    } else {
      valorBase = f[campoFiltro] ?? "";
    }

    return String(valorBase).toLowerCase().includes(texto);
  });

  async function handleExcluir(fornecedorId) {
    if (!fornecedorId) return;

    const confirmar = window.confirm(
      "Tem certeza que deseja excluir este fornecedor? Essa ação não pode ser desfeita."
    );
    if (!confirmar) return;

    setCarregando(true);
    setErro("");

    try {
      await deleteDoc(doc(db, "fornecedores", fornecedorId));
      setFornecedores((prev) => prev.filter((f) => f.id !== fornecedorId));
    } catch (e) {
      console.error("Erro ao excluir fornecedor:", e);
      setErro("Erro ao excluir fornecedor. Tente novamente.");
    } finally {
      setCarregando(false);
    }
  }

  const itemsParaCards = fornecedoresFiltrados.map((f) => ({
    id: f.id,
    nome: f.nome || "Sem nome",
    subtitulo: f.contato || "-",
    detalhes: [
      { icone: "🔢", label: "Código", valor: renderCodigo(f) },
      { icone: "📞", label: "Telefone", valor: f.telefone || "-" },
      { icone: "✉️", label: "Email", valor: f.email || "-" },
      { icone: "🏢", label: "Endereço", valor: f.endereco || "-" },
      {
        icone: "💳",
        label: "Fatura",
        valor: f.fatura ? "Sim" : "Não",
      },
    ],
  }));

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Fornecedores"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
        abaBottom="cadastros"
      >
        <CardListMobile
          items={itemsParaCards}
          carregando={carregando}
          erro={erro}
          carregandoMessage="Carregando fornecedores..."
          titulo="Fornecedores"
          searchPlaceholder="Buscar fornecedor..."
          novoLabel="Novo Fornecedor"
          emptyMessage="Nenhum fornecedor encontrado"
          onHome={() => navigate("/")}
          onNovo={() => navigate("/cadastros/fornecedores/novo")}
          onEditar={(id) => navigate(`/cadastros/fornecedores/editar/${id}`)}
          getSearchText={(item) =>
            `${item.nome} ${item.subtitulo}`
          }
        />
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop tema={tema} titulo="Fornecedores" subtitulo="">
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Fornecedores cadastrados</h3>
            <p>Lista de fornecedores por empresa, vinculados ao grupo atual.</p>
          </div>

          <div className="pc-table-header-actions">
            <Can modulo="cadastros" recurso="fornecedores" acao="form">
<button
              type="button"
              className="pc-btn-primario"
              onClick={() => navigate("/cadastros/fornecedores/novo")}
            >
              + Novo Fornecedor
            </button>
</Can>
          </div>
        </header>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          onAdicionarClick={() => navigate("/cadastros/fornecedores/novo")}
          placeholder="Filtrar fornecedores..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          {erro && (
            <div
              style={{
                background: "#7f1d1d",
                color: "#fecaca",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {carregando ? (
            <p style={{ padding: 10 }}>Carregando fornecedores...</p>
          ) : fornecedoresFiltrados.length === 0 ? (
            <p style={{ padding: 10 }}>Nenhum fornecedor encontrado.</p>
          ) : (
            <table className="pc-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Contato</th>
                  <th>Telefone</th>
                  <th>Email</th>
                  <th>Endereço</th>
                  <th>Fatura</th>
                  <th>Observações</th>
                  <th style={{ textAlign: "right" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {fornecedoresFiltrados.map((f) => (
                  <tr key={f.id}>
                    <td>{renderCodigo(f)}</td>
                    <td>{f.nome || "-"}</td>
                    <td>{f.contato || "-"}</td>
                    <td>{f.telefone || "-"}</td>
                    <td>{f.email || "-"}</td>
                    <td>{f.endereco || "-"}</td>
                    <td>{f.fatura ? "Sim" : "Não"}</td>
                    <td>{f.observacoes || "-"}</td>
                    <td
                      style={{
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Can modulo="cadastros" recurso="fornecedores" acao="altera">
<button
                        type="button"
                        title="Editar fornecedor"
                        onClick={() =>
                          navigate(`/cadastros/fornecedores/editar/${f.id}`)
                        }
                        style={{
                          border: "none",
                          
                          cursor: "pointer",
                          fontSize: 18,
                          marginRight: 8,
                        }}
                      >
                        ✏️
                      </button>
</Can>
                      <Can modulo="cadastros" recurso="fornecedores" acao="altera">
<button
                        type="button"
                        title="Apagar fornecedor"
                        onClick={() => handleExcluir(f.id)}
                        style={{
                          border: "none",
                          
                          cursor: "pointer",
                          fontSize: 18,
                        }}
                      >
                        🗑️
                      </button>
</Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default FornecedoresPage;
